import React, { useLayoutEffect, useRef } from 'react';

type PasteTarget = HTMLTextAreaElement | HTMLInputElement;

interface Snapshot {
  value: string;
  selectionStart: number;
  selectionEnd: number;
  selectionDirection: 'forward' | 'backward' | 'none';
}

interface Edit {
  before: Snapshot;
  after: Snapshot;
}

interface UseTrimmedPasteUndoProps {
  enabled: boolean;
  value: string;
  onValueChange: (value: string) => void;
}

function snapshot(target: PasteTarget): Snapshot {
  return {
    value: target.value,
    selectionStart: target.selectionStart ?? target.value.length,
    selectionEnd: target.selectionEnd ?? target.value.length,
    selectionDirection: target.selectionDirection ?? 'none',
  };
}

function useTrimmedPasteUndo<T extends PasteTarget = HTMLTextAreaElement>({
  enabled, value, onValueChange,
}: UseTrimmedPasteUndoProps) {
  const targetRef = useRef<T | null>(null);
  const historyRef = useRef<Edit[]>([]);
  const indexRef = useRef(0);
  const currentRef = useRef<Snapshot>({
    value, selectionStart: value.length, selectionEnd: value.length, selectionDirection: 'none',
  });
  const beforeRef = useRef<Snapshot | null>(null);
  const compositionRef = useRef<Snapshot | null>(null);
  const pendingSelectionRef = useRef<Snapshot | null>(null);
  const enabledRef = useRef(enabled);

  const pushEdit = (before: Snapshot, after: Snapshot) => {
    currentRef.current = after;
    if (before.value === after.value) return;
    historyRef.current = historyRef.current.slice(0, indexRef.current);
    historyRef.current.push({ before, after });
    indexRef.current += 1;
  };

  const restoreSelection = (entry: Snapshot) => {
    targetRef.current?.setSelectionRange(entry.selectionStart, entry.selectionEnd, entry.selectionDirection);
  };

  const applyValue = (entry: Snapshot) => {
    currentRef.current = entry;
    pendingSelectionRef.current = entry;
    onValueChange(entry.value);
    if (targetRef.current?.value === entry.value) {
      restoreSelection(entry);
      pendingSelectionRef.current = null;
    }
  };

  const step = (undo: boolean) => {
    beforeRef.current = null;
    const nextIndex = indexRef.current + (undo ? -1 : 1);
    if (nextIndex < 0 || nextIndex > historyRef.current.length) {
      applyValue(currentRef.current);
      return;
    }
    const edit = historyRef.current[undo ? nextIndex : indexRef.current];
    indexRef.current = nextIndex;
    applyValue(undo ? edit.before : edit.after);
  };

  useLayoutEffect(() => {
    if (value !== currentRef.current.value || enabled !== enabledRef.current) {
      historyRef.current = [];
      indexRef.current = 0;
      currentRef.current = targetRef.current ? snapshot(targetRef.current) : {
        value, selectionStart: value.length, selectionEnd: value.length, selectionDirection: 'none',
      };
      beforeRef.current = null;
      compositionRef.current = null;
      pendingSelectionRef.current = null;
    }
    enabledRef.current = enabled;
    const selection = pendingSelectionRef.current;
    if (selection && targetRef.current?.value === selection.value) {
      restoreSelection(selection);
      pendingSelectionRef.current = null;
    }
  });

  const recordInput = (inputType?: string): boolean => {
    if (!enabled) return false;
    if (inputType === 'historyUndo' || inputType === 'historyRedo') {
      const target = targetRef.current;
      if (target) target.value = currentRef.current.value;
      step(inputType === 'historyUndo');
      return true;
    }
    const target = targetRef.current;
    if (!target || target.value === currentRef.current.value) return false;
    const after = snapshot(target);
    if (compositionRef.current) {
      currentRef.current = after;
      return false;
    }
    pushEdit(beforeRef.current ?? currentRef.current, after);
    beforeRef.current = null;
    return false;
  };

  useLayoutEffect(() => {
    const target = targetRef.current;
    if (!enabled || !target) return undefined;
    const beforeInput = (nativeEvent: Event) => {
      const event = nativeEvent as InputEvent;
      beforeRef.current = snapshot(target);
      currentRef.current = beforeRef.current;
      if (event.inputType === 'historyUndo' || event.inputType === 'historyRedo') {
        if (event.cancelable && !compositionRef.current) {
          event.preventDefault();
          step(event.inputType === 'historyUndo');
        }
      }
    };
    const captureSelection = () => {
      if (target.value === currentRef.current.value) {
        beforeRef.current = snapshot(target);
        currentRef.current = beforeRef.current;
      }
    };
    const compositionStart = () => {
      compositionRef.current = snapshot(target);
    };
    const compositionEnd = () => {
      if (compositionRef.current) {
        pushEdit(compositionRef.current, snapshot(target));
      }
      compositionRef.current = null;
      beforeRef.current = null;
    };
    target.addEventListener('beforeinput', beforeInput);
    target.addEventListener('select', captureSelection);
    target.addEventListener('pointerdown', captureSelection);
    target.addEventListener('cut', captureSelection);
    target.addEventListener('blur', captureSelection);
    target.addEventListener('compositionstart', compositionStart);
    target.addEventListener('compositionend', compositionEnd);
    return () => {
      target.removeEventListener('beforeinput', beforeInput);
      target.removeEventListener('select', captureSelection);
      target.removeEventListener('pointerdown', captureSelection);
      target.removeEventListener('cut', captureSelection);
      target.removeEventListener('blur', captureSelection);
      target.removeEventListener('compositionstart', compositionStart);
      target.removeEventListener('compositionend', compositionEnd);
    };
  });

  const onPaste = (event: React.ClipboardEvent<T>): boolean => {
    if (!enabled || compositionRef.current) return false;
    const text = event.clipboardData.getData('text');
    if (text === '') return false;
    event.preventDefault();
    const before = snapshot(event.currentTarget);
    const pasted = text.trim();
    const nextValue = before.value.slice(0, before.selectionStart) + pasted + before.value.slice(before.selectionEnd);
    const caret = before.selectionStart + pasted.length;
    const after: Snapshot = {
      value: nextValue, selectionStart: caret, selectionEnd: caret, selectionDirection: 'none',
    };
    pushEdit(before, after);
    beforeRef.current = null;
    if (before.value !== after.value) {
      applyValue(after);
    } else {
      restoreSelection(after);
    }
    return true;
  };

  const onKeyDown = (event: React.KeyboardEvent<T>) => {
    if (!enabled || compositionRef.current || event.nativeEvent.isComposing) return;
    beforeRef.current = snapshot(event.currentTarget);
    currentRef.current = beforeRef.current;
    if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
    const key = event.key.toLowerCase();
    const isUndo = key === 'z' && !event.shiftKey;
    const isRedo = (key === 'z' && event.shiftKey) || (key === 'y' && event.ctrlKey && !event.shiftKey);
    if (!isUndo && !isRedo) return;
    event.preventDefault();
    step(isUndo);
  };

  return {
    targetRef, onPaste, onKeyDown, recordInput,
  };
}

export default useTrimmedPasteUndo;
